// src/api/apiService.js
import axios from 'axios';

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'https://app.craftmyskin.com/api';

/* -----------------------------------------------------------------
   1. Axios instance
   ----------------------------------------------------------------- */
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

/* -----------------------------------------------------------------
   2. Public init function – call **once** after the store exists
   ----------------------------------------------------------------- */
let _initialized = false;
let _store = null; // Store reference for use in login/logout
export const initAuthInterceptor = (store) => {
  if (_initialized) return;
  _initialized = true;
  _store = store; // Store reference

  // ---- token subscription ------------------------------------------------
  let currentToken = store.getState().accessToken;
  console.log('import.meta.env.VITE_API_BASE_URL', import.meta.env.VITE_API_BASE_URL);
  console.log('initAuthInterceptor currentToken', currentToken);
  const unsubscribe = store.subscribe(
    (s) => s.accessToken,
    (token) => {
      currentToken = token;
      console.log('initAuthInterceptor currentToken', currentToken);
      api.defaults.headers.common['Authorization'] = token ? `Bearer ${token}` : '';
    }
  );

  // set initial header
  if (currentToken) {
    api.defaults.headers.common['Authorization'] = `Bearer ${currentToken}`;
  }

  // ---- request interceptor to ensure token is always added ---------------
api.interceptors.request.use(
  (config) => {
      const token = store.getState().accessToken;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

  // ---- refresh-queue state -----------------------------------------------
let isRefreshing = false;
let refreshSubscribers = [];

const subscribeTokenRefresh = (cb) => refreshSubscribers.push(cb);
const onRefreshed = (newToken) => {
  refreshSubscribers.forEach((cb) => cb(newToken));
  refreshSubscribers = [];
};

  // ---- response interceptor (401 → refresh) -----------------------------
api.interceptors.response.use(
    (res) => res,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
        const { refreshToken, setAuth, logout } = store.getState();

      if (!refreshToken) {
        logout();
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh((newToken) => {
            if (!newToken) return reject(error);
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            resolve(api(originalRequest));
          });
        });
      }

      isRefreshing = true;
      
      try {
        const res = await axios.post(`${API_BASE_URL}/auth/token/refresh/`, {
          refresh: refreshToken,
        });

        const newAccess = res.data.access;
          if (!newAccess) throw new Error('No access token in refresh response');

        // update Zustand
          setAuth({ accessToken: newAccess, refreshToken });

          // notify queue
        onRefreshed(newAccess);
        isRefreshing = false;

          // retry original request
        originalRequest.headers.Authorization = `Bearer ${newAccess}`;
        return api(originalRequest);
        } catch (refreshError) {
          store.getState().logout();
          onRefreshed(null);
          isRefreshing = false;
          return Promise.reject(refreshError);
        }
    }

    return Promise.reject(error);
  }
);

  // ---- final 401 → logout ------------------------------------------------
  api.interceptors.response.use(
    (res) => res,
    (err) => {
      if (err.response?.status === 401) {
        store.getState().logout();
        window.location.href = '/login';
      }
      return Promise.reject(err);
    }
  );
};

/* -----------------------------------------------------------------
   3. Helper wrapper – same API you already used everywhere
   ----------------------------------------------------------------- */
export const apiService = {
  get: (url, opts = {}) => api.get(url, opts),
  post: (url, data, opts = {}) => api.post(url, data, opts),
  patch: (url, data, opts = {}) => api.patch(url, data, opts),
  put: (url, data, opts = {}) => api.put(url, data, opts),
  delete: (url, opts = {}) => api.delete(url, opts),
};

/* -----------------------------------------------------------------
   4. All endpoint functions – **unchanged** (copy-paste from yours)
   ----------------------------------------------------------------- */
export const login = async (credentials) => {
  const { data } = await api.post('/auth/login/', credentials);
  const { access, refresh, user } = data;
  if (_store) {
    _store.getState().setAuth({ accessToken: access, refreshToken: refresh, user });
  }
  return data;
};

export const register = async (userData) => api.post('/auth/register', userData).then((r) => r.data);

export const logout = async () => {
  if (!_store) return;
  const { refreshToken, logout: storeLogout } = _store.getState();
  try {
    if (refreshToken) {
      await api.post('/auth/logout/', { refresh: refreshToken });
    }
  } catch (e) {
    console.warn('Logout endpoint failed', e);
  } finally {
    storeLogout();
  }
};

export const refreshAuthToken = async (refreshToken) =>
  api.post('/auth/token/refresh/', { refresh: refreshToken }).then((r) => r.data);

/* ---------- USER PROFILE ---------- */
export const getProfile = async () => api.get('/api/profile/').then((r) => r.data);
export const updateProfile = async (data) => apiService.put('/profile/', data);

/* ---------- PASSWORD / EMAIL ---------- */
export const requestPasswordReset = async (email) =>
  apiService.post('/auth/password/request-reset/', { email, platform: 'admin' });
export const forgotPassword = requestPasswordReset;
export const confirmPasswordReset = async (uid, token, newPassword) =>
  apiService.post(`/auth/password/reset/${encodeURIComponent(uid)}/${encodeURIComponent(token)}/`, {
    new_password: newPassword,
  });
export const resetPassword = requestPasswordReset;
export const verifyEmail = async (email) => apiService.post('/user/verify-email', { email });
export const changePassword = async (newPassword) =>
  apiService.post('/user/change-password', { newPassword });

/* ---------- ADMIN USER MANAGEMENT ---------- */
export const getAdmins = async (params = {}) => {
  const res = await apiService.get('/admins/', { params });
  return res.data;
};
export const createAdmin = async ({ full_name, email }) => {
  const res = await apiService.post('/admins/', { full_name, email });
  return res.data;
};
/** @deprecated Prefer getAdmins / createAdmin */
export const getUsers = getAdmins;
export const createUser = createAdmin;
export const updateUser = async (id, data) => apiService.put(`/admins/${id}/`, data);
export const deleteUser = async (id) => apiService.delete(`/admins/${id}/`);

/* ---------- SERVER LOGS ---------- */
export const getLogs = async (params = {}) => {
  const res = await apiService.get('/logs/', { params });
  const data = res.data;
  if (Array.isArray(data)) {
    return { count: data.length, next: null, previous: null, results: data };
  }
  if (data?.results) return data;
  if (Array.isArray(data?.data)) {
    return {
      count: data.count ?? data.data.length,
      next: data.next ?? null,
      previous: data.previous ?? null,
      results: data.data,
    };
  }
  return { count: 0, next: null, previous: null, results: [] };
};

/* ---------- INGREDIENTS ---------- */
export const getIngredients = async () => apiService.get('/products-info/ingredients/');

/* ---------- ACCREDITATIONS ---------- */
export const getProductAccreditationTypes = async () => apiService.get('/products-info/certifications/');

/* ---------- PRODUCT FORMULATION TYPES / CATEGORIES ---------- */
export const getProductFormulationTypes = async () => apiService.get('/products-info/formulation-types/');

/* ---------- PRODUCT CATEGORIES ---------- */
export const getProductCategories = async (params = {}) => {
  const res = await apiService.get('/products-info/product-categories/', { params });
  return res.data;
};

/* ---------- PRODUCTS ---------- */
export const createProduct = async (data) => {
  return console.log(data);
  apiService.post('/products', data);
}

export const updateProduct = async (slug, data) => apiService.patch(`/products/${slug}/`, data, {
  headers: { 'Content-Type': 'multipart/form-data' }
});
export const deleteProduct = async (slug) => apiService.delete(`/products/${slug}/`);
export const getProductBySlug = async (slug) => apiService.get(`/products/${slug}/`);

/** Validate one or more products by slug. POST body: { slugs: string[] } */
export const validateProducts = async (slugs) => {
  const res = await apiService.post('products-validate/validate/', { slugs });
  return res.data;
};

/* ---------- EDUCATION INFO / CARD TYPES ---------- */
export const createCardType = async (data) =>
  apiService.post('/education-info/card-types/', data).then((r) => r.data);

export const getCardTypes = async (params = {}) => {
  const res = await apiService.get('/education-info/card-types/', { params });
  return res.data;
};

export const getCardTypeBySlug = async (slug) =>
  apiService.get(`/education-info/card-types/${slug}/`).then((r) => r.data);

export const updateCardType = async (slug, data) =>
  apiService.patch(`/education-info/card-types/${slug}/`, data).then((r) => r.data);

export const deleteCardType = async (slug) =>
  apiService.delete(`/education-info/card-types/${slug}/`).then((r) => r.data);

/* ---------- EDUCATION INFO / RULE TYPES ---------- */
export const createRuleType = async (data) =>
  apiService.post('/education-info/rule-types/', data).then((r) => r.data);

export const getRuleTypes = async (params = {}) => {
  const res = await apiService.get('/education-info/rule-types/', { params });
  return res.data;
};

export const getRuleTypeBySlug = async (slug) =>
  apiService.get(`/education-info/rule-types/${slug}/`).then((r) => r.data);

export const updateRuleType = async (slug, data) =>
  apiService.patch(`/education-info/rule-types/${slug}/`, data).then((r) => r.data);

export const deleteRuleType = async (slug) =>
  apiService.delete(`/education-info/rule-types/${slug}/`).then((r) => r.data);

/* ---------- EDUCATION INFO / PLACEMENTS ---------- */
export const createPlacement = async (data) =>
  apiService.post('/education-info/placements/', data).then((r) => r.data);

export const getPlacements = async (params = {}) => {
  const res = await apiService.get('/education-info/placements/', { params });
  return res.data;
};

export const getPlacementBySlug = async (slug) =>
  apiService.get(`/education-info/placements/${slug}/`).then((r) => r.data);

export const updatePlacement = async (slug, data) =>
  apiService.patch(`/education-info/placements/${slug}/`, data).then((r) => r.data);

export const deletePlacement = async (slug) =>
  apiService.delete(`/education-info/placements/${slug}/`).then((r) => r.data);

/* ---------- EDUCATION INFO / RULES ---------- */
export const createRule = async (data) =>
  apiService.post('/education-info/rules/', data).then((r) => r.data);

export const getRules = async (params = {}) => {
  const res = await apiService.get('/education-info/rules/', { params });
  return res.data;
};

export const getRuleBySlug = async (slug) =>
  apiService.get(`/education-info/rules/${slug}/`).then((r) => r.data);

export const updateRule = async (slug, data) =>
  apiService.patch(`/education-info/rules/${slug}/`, data).then((r) => r.data);

// Note: backend spec uses `/rule/:slug/` (singular) for delete.
export const deleteRule = async (slug) =>
  apiService.delete(`/education-info/rule/${slug}/`).then((r) => r.data);

/* ---------- EDUCATION INFO / CARDS ---------- */
export const createCard = async (formData) =>
  apiService
    .post('/education-info/cards/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    .then((r) => r.data);

export const getCards = async (params = {}) => {
  const res = await apiService.get('/education-info/cards/', { params });
  return res.data;
};

export const getCardBySlug = async (slug) =>
  apiService.get(`/education-info/cards/${slug}/`).then((r) => r.data);

export const updateCard = async (slug, formData) =>
  apiService
    .patch(`/education-info/cards/${slug}/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    .then((r) => r.data);

export const deleteCard = async (slug) =>
  apiService.delete(`/education-info/cards/${slug}/`).then((r) => r.data);

/* ---------- EDUCATION INFO / TAGS ---------- */
export const createTag = async (data) =>
  apiService.post('/education-info/tags/', data).then((r) => r.data);

export const getTags = async (params = {}) => {
  const res = await apiService.get('/education-info/tags/', { params });
  return res.data;
};

export const getTagBySlug = async (slug) =>
  apiService.get(`/education-info/tags/${slug}/`).then((r) => r.data);

export const updateTag = async (slug, data) =>
  apiService.patch(`/education-info/tags/${slug}/`, data).then((r) => r.data);

export const deleteTag = async (slug) =>
  apiService.delete(`/education-info/tags/${slug}/`).then((r) => r.data);

/* ---------- EDUCATION INFO / MEDIA TYPES ---------- */
export const createMediaType = async (data) =>
  apiService.post('/education-info/media-types/', data).then((r) => r.data);

export const getMediaTypes = async (params = {}) => {
  const res = await apiService.get('/education-info/media-types/', { params });
  return res.data;
};

export const getMediaTypeBySlug = async (slug) =>
  apiService.get(`/education-info/media-types/${slug}/`).then((r) => r.data);

export const updateMediaType = async (slug, data) =>
  apiService.patch(`/education-info/media-types/${slug}/`, data).then((r) => r.data);

export const deleteMediaType = async (slug) =>
  apiService.delete(`/education-info/media-types/${slug}/`).then((r) => r.data);

/* ---------- EDUCATION INFO / GROUPS ---------- */
export const createGroup = async (data) =>
  apiService.post('/education-info/groups/', data).then((r) => r.data);

export const getGroups = async (params = {}) => {
  const res = await apiService.get('/education-info/groups/', { params });
  return res.data;
};

export const getGroupBySlug = async (slug) =>
  apiService.get(`/education-info/groups/${slug}/`).then((r) => r.data);

export const updateGroup = async (slug, data) =>
  apiService.patch(`/education-info/groups/${slug}/`, data).then((r) => r.data);

export const deleteGroup = async (slug) =>
  apiService.delete(`/education-info/groups/${slug}/`).then((r) => r.data);

/* ---------- BRANDS ---------- */
export const getBrands = async (params = {}) => {
  const res = await apiService.get('/products-info/brands/', { params });
  return res.data;
};

/* ---------- ROUTINE / QUESTIONNAIRE QUESTIONS ---------- */
export const getRoutineFrequency = async () => apiService.get('/products-info/prescription-frequencies/');
export const getRoutineApplicationPeriod = async () => apiService.get('/products-info/prescription-periods/');
// Backed by the new questionnaire-info endpoint so old callers don’t 404
export const getRoutineQuestions = async (params = {}) =>
  apiService.get('/questionnaire-info/questions', { params });
export const getRoutineAmounts = async (params = {}) => {
  const res = await apiService.get('/products-info/prescription-amounts/', { params });
  return res.data;
};
export const getRoutineApplicationAreas = async () => apiService.get('/products-info/prescription-areas/');

// Questionnaire questions (paginated, with optional search) – convenience helper returning data only
export const getQuestionnaireInfoQuestions = async ({ page = 1, pageSize = 10, search = '' } = {}) => {
  const params = {
    page,
    page_size: pageSize,
  };
  if (search) params.search = search;

  // Returns full paginated payload: { count, next, previous, results: [...] }
  const res = await apiService.get('/questionnaire-info/questions', { params });
  return res.data;
};

// Questionnaire question types (for dynamic dropdowns)
export const getQuestionnaireQuestionTypes = async () =>
  apiService.get('/questionnaire-info/question-types/').then((r) => r.data);

// Create questionnaire question at /questionnaire-info/questions/
export const createQuestionnaireQuestion = async (payload) =>
  apiService.post('/questionnaire-info/questions/', payload).then((r) => r.data);

// Update questionnaire question (admin docs: PATCH)
export const updateQuestionnaireQuestion = async (slug, payload) =>
  apiService.patch(`/questionnaire-info/questions/${slug}/`, payload).then((r) => r.data);

// Delete questionnaire question
export const deleteQuestionnaireQuestion = async (slug) =>
  apiService.delete(`/questionnaire-info/questions/${slug}/`).then((r) => r.data);

/* ---------- QUESTIONNAIRES ---------- */
// Get all questionnaires
export const getQuestionnaires = async () =>
  apiService.get('/questionnaire').then((r) => r.data);

// Get questions for a specific questionnaire by slug
export const getQuestionnaireQuestions = async (slug) =>
  apiService.get(`/questionnaire/${slug}/questions/`).then((r) => r.data);

/** Create questionnaire — POST /questionnaire/ { name, description } */
export const createQuestionnaire = async (payload) =>
  apiService.post('/questionnaire/', payload).then((r) => r.data);

/**
 * Update questionnaire question membership / order.
 * PATCH /questionnaire/:slug/ { question_slugs: string[] }
 * Send the full desired ordered list (additions and removals).
 */
export const updateQuestionnaire = async (slug, payload) =>
  apiService.patch(`/questionnaire/${slug}/`, payload).then((r) => r.data);

export const createQuestion = async (data) => apiService.post('/routine/questions', data);
export const updateQuestion = async (id, data) => apiService.put(`/routine/questions/${id}`, data);
export const deleteQuestion = async (id) => apiService.delete(`/routine/questions/${id}`);
export const submitRoutineResponse = async (data) => apiService.post('/routine/responses', data);
export const getRoutineResponses = async () => apiService.get('/routine/responses');

/* ---------- ORDERS ---------- */
export const fetchOrders = async () => apiService.get('/orders');
export const updateOrderStatus = async (id, status) =>
  apiService.put(`/orders/${id}/status`, { status });
export const updateDermatologistApproval = async (id, approved, notes) =>
  apiService.put(`/orders/${id}/dermatologist-review`, { approved, notes });

/* ---------- RETAILERS / STORES ---------- */
/** Admin creates a store + retailer_owner user. Body shape follows backend contract. */
export const registerRetailerStore = async (data) =>
  apiService.post('/retailer/stores/', data).then((r) => r.data);

/**
 * Normalize retailer store list payloads to DRF-like { results, count } for the admin table.
 * Backends may return results, data[], stores[], items[], records[], or a bare array.
 */
function normalizeRetailerStoresListPayload(raw) {
  if (raw == null) return { results: [], count: 0 };
  if (Array.isArray(raw)) return { results: raw, count: raw.length };

  let body = raw;
  if (body && typeof body === 'object' && 'data' in body) {
    const inner = body.data;
    if (Array.isArray(inner)) {
      const countFromOuter =
        typeof body.count === 'number'
          ? body.count
          : typeof body.total === 'number'
            ? body.total
            : inner.length;
      return { results: inner, count: countFromOuter };
    }
    if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
      body = inner;
    }
  }

  const results = [body.results, body.stores, body.items, body.records, Array.isArray(body.data) ? body.data : null].find(
    Array.isArray
  ) ?? [];

  const count =
    typeof body.count === 'number'
      ? body.count
      : typeof body.total === 'number'
        ? body.total
        : typeof body.total_count === 'number'
          ? body.total_count
          : results.length;

  return { results, count };
}

/**
 * List stores (admin). Supports pagination and `requires-validation` (boolean) per API docs:
 * e.g. GET /retailer-info/stores/?requires-validation=true
 */
export const getRetailerInfoStores = async (params = {}) => {
  const res = await apiService.get('/retailer-info/stores/', { params });
  return normalizeRetailerStoresListPayload(res.data);
};

/** List store types used by retailer registration form. */
export const getRetailerStoreTypes = async () => {
  const res = await apiService.get('/retailer-info/store-types/');
  return res.data;
};

/** Full admin-level store detail (verification notes, validated_by, etc.). */
export const getRetailerInfoStoreBySlug = async (slug) =>
  apiService.get(`/retailer-info/stores/${slug}/`).then((r) => r.data);

/** Admin partial update (e.g. verification_notes). Requires can_validate_store. */
export const updateRetailerInfoStore = async (slug, data) =>
  apiService.patch(`/retailer-info/stores/${slug}/`, data).then((r) => r.data);

/**
 * Approve store: sets is_approved, requires_validation=false, sends approval email.
 * Response example: { message: "Store validated and approved successfully." }
 */
export const validateRetailerStore = async (slug) =>
  apiService.patch(`/retailer-info/stores/${slug}/validate/`).then((r) => r.data);

/* ---------- NOTIFICATIONS (ADMIN) ---------- */
/**
 * Send batch push notifications.
 * POST /notifications/batch-send/
 * Body example:
 * {
 *   title, body, role?, users?: string[],
 *   data_payload?: { type?, priority?, ... }
 * }
 */
export const sendBatchNotifications = async (payload) => {
  const res = await apiService.post('/notifications/batch-send/', payload);
  return res.data;
};

/* -----------------------------------------------------------------
   5. Default export (kept for backward compatibility)
   ----------------------------------------------------------------- */
export default api;
